/**
 * A track's colour, derived from its id.
 *
 * Derived rather than stored, so it is identical on every screen, in every
 * session, and in the PDF report — without a migration, a colour picker, or a
 * field that can drift out of sync with the document someone printed last
 * month. A track that is blue in the app and orange in the report makes the two
 * impossible to discuss side by side, which is the whole point of generating
 * one (T-404).
 *
 * Colour never carries meaning on its own: every series is named in a legend
 * and available as a table.
 */

const SERIES_COUNT = 8

/**
 * FNV-1a, 32-bit.
 *
 * Any stable hash would do; this one is four lines and has no dependency. What
 * matters is that it is a pure function of the id, so it produces the same
 * answer on this device, on the next one, and in a year.
 */
function hash(value: string): number {
  let result = 0x811c9dc5
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index)
    result = Math.imul(result, 0x01000193)
  }
  return result >>> 0
}

/** A CSS custom property reference, so the colour still follows the theme. */
export function trackColour(trackId: string): string {
  return `var(--chart-${String((hash(trackId) % SERIES_COUNT) + 1)})`
}

/** Distinct colours for the options of one enum track, in order. */
export function categoryColour(index: number): string {
  return `var(--chart-${String((index % SERIES_COUNT) + 1)})`
}

/**
 * Dash patterns, so an overlay survives a greyscale printer.
 *
 * These reports get printed on office printers, and eight distinct hues become
 * eight indistinguishable greys (T-504). Colour is therefore never the only
 * thing separating two lines: the pattern is, and the legend carries it too so
 * the two can still be matched up by eye.
 *
 * Solid first, because a single-series chart should not look dashed for no
 * reason.
 */
const DASHES: readonly string[] = [
  '',
  '6 3',
  '2 3',
  '9 3 2 3',
  '1 3',
  '12 4',
  '6 3 2 3 2 3',
  '4 2 1 2',
]

/** `undefined` for a solid line, which is what SVG wants for "no dashes". */
export function seriesDash(index: number): string | undefined {
  const pattern = DASHES[index % DASHES.length]
  return pattern === '' ? undefined : pattern
}
